import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Button, Typography } from '@pesto/ui';
import { Badge } from '@pesto/ui/components/Badge';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import {
  AchReturnType,
  GetLoanTransactionsDocument,
  SortDirection,
  useAchReturnMutation,
  useGetLoanAchRequestsQuery,
  useGetLoanStatementsQuery,
  type AchReturnMutation,
  type AchReturnMutationVariables,
  type GetLoanStatementsQuery,
} from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { routes } from '../../constants/routes';

type Step = 'details' | 'preview' | 'complete';

const RETURN_TYPE_OPTIONS = [
  { value: AchReturnType.BankReturn, label: 'Bank Return' },
  { value: AchReturnType.AdminVoid, label: 'Admin Void' },
] as const;

const schema = z
  .object({
    achRequestId: z.string().trim().min(1, 'ACH request ID is required'),
    returnCode: z.string().optional(),
    returnType: z.nativeEnum(AchReturnType),
    returnedOn: z.string().optional(),
    notes: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.returnType !== AchReturnType.BankReturn) return;

    if (!values.returnCode?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['returnCode'],
        message: 'Return code is required for bank returns',
      });
    }

    if (!values.returnedOn?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['returnedOn'],
        message: 'Returned on date is required for bank returns',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

type AchReturnResult = NonNullable<AchReturnMutation['achReturn']>;

type AllocationItem = NonNullable<NonNullable<AchReturnResult['allocationsToReverse']>[number]>;

type AdjustmentItem = NonNullable<NonNullable<AchReturnResult['interestAdjustments']>[number]>;

type UserStatementFromQuery = NonNullable<NonNullable<GetLoanStatementsQuery['loanStatements']>[number]>;

const formatCurrencyFromCents = (amountCents?: number | null) => {
  if (amountCents === undefined || amountCents === null) return '-';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
};

const formatReturnFee = (result: AchReturnResult) =>
  result.returnFeeWillPost ? formatCurrencyFromCents(result.returnFeeCents) : 'No fee will post';

const normalizeOptionalText = (value?: string) => {
  const trimmed = value?.trim();

  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};

const normalizeReturnCode = (value?: string) => {
  const trimmed = value?.trim();

  return trimmed && trimmed.length > 0 ? trimmed.toUpperCase() : undefined;
};

const normalizeFormValues = (values: FormValues): FormValues => ({
  achRequestId: values.achRequestId.trim(),
  returnCode: normalizeReturnCode(values.returnCode),
  returnType: values.returnType,
  returnedOn: values.returnType === AchReturnType.BankReturn ? normalizeOptionalText(values.returnedOn) : undefined,
  notes: normalizeOptionalText(values.notes),
});

const compact = <T,>(items?: Array<T | null> | null): T[] => {
  return (items ?? []).filter((item): item is T => item !== null);
};

const formatCurrencyFromAmount = (amount?: string | null) => {
  if (!amount) return '-';
  const parsed = Number(amount);

  if (Number.isNaN(parsed)) return amount;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(parsed);
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
      <Typography variant="bodySmall" className="text-muted-foreground">
        {label}
      </Typography>
      <Typography variant="bodySmall" className="text-right font-medium">
        {value}
      </Typography>
    </div>
  );
};

const StepIndicator = ({ step }: { step: Step }) => {
  const currentStepNumber = step === 'details' ? 1 : step === 'preview' ? 2 : 3;
  const labels = ['Details', 'Preview', 'Complete'];

  return (
    <div className="flex flex-wrap gap-2">
      {labels.map((label, index) => {
        const stepNumber = index + 1;

        const variant =
          stepNumber === currentStepNumber ? 'default' : stepNumber < currentStepNumber ? 'success' : 'outline';

        return (
          <Badge key={label} variant={variant}>
            {stepNumber}. {label}
          </Badge>
        );
      })}
    </div>
  );
};

export const ReturnAchRequestComponent = ({ payload, api }: ModalProps<LoanModalPayload, AchReturnResult>) => {
  const { loanId } = payload;

  const { data: userStatementsData, loading: userStatementsLoading } = useGetLoanStatementsQuery({
    variables: { loanId },
    skip: !loanId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: achRequestsData, loading: achRequestsLoading } = useGetLoanAchRequestsQuery({
    variables: {
      loanId,
      insertedAtSortDirection: SortDirection.Desc,
      paginationDetailsOnly: false,
      resultsPerPage: 100,
      pageNumber: 1,
    },
    skip: !loanId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [runReturnAchRequest, { loading }] = useAchReturnMutation();
  const { handleMutation } = useMutationWithToast();

  const confirmRefetchQueries = useMemo(() => {
    return [
      ...getLoanRefetchQuery(loanId).refetchQueries.filter((query) => query.query !== GetLoanTransactionsDocument),
      'getUserTransactions',
    ];
  }, [loanId]);

  const [step, setStep] = useState<Step>('details');
  const [submittedValues, setSubmittedValues] = useState<FormValues | null>(null);
  const [previewResult, setPreviewResult] = useState<AchReturnResult | null>(null);
  const [executionResult, setExecutionResult] = useState<AchReturnResult | null>(null);

  const statementsById = useMemo(() => {
    const entries = (userStatementsData?.loanStatements ?? [])
      .filter((statement): statement is UserStatementFromQuery => Boolean(statement))
      .map((statement) => [statement.id, statement] as const);

    return new Map(entries);
  }, [userStatementsData?.loanStatements]);

  const achRequestOptions = useMemo(
    () =>
      (achRequestsData?.loanAchRequests?.achRequests ?? []).map((request) => ({
        value: request.id,
        label: `${request.id} | ${formatCurrencyFromCents(request.amountCents)} | ${request.status || 'Unknown'} | ${formatDate(request.insertedAt)}`,
      })),
    [achRequestsData?.loanAchRequests?.achRequests],
  );

  const achRequestHelperText = useMemo(() => {
    if (achRequestsLoading) return 'Loading ACH requests...';

    if (achRequestOptions.length === 0) return 'No ACH requests found for this user.';

    return undefined;
  }, [achRequestsLoading, achRequestOptions.length]);

  const runAchReturn = (values: FormValues, dryRun: boolean) => {
    const variables: AchReturnMutationVariables = {
      achRequestId: values.achRequestId,
      returnCode: normalizeReturnCode(values.returnCode),
      returnType: values.returnType,
      returnedOn: values.returnedOn,
      notes: normalizeOptionalText(values.notes),
      dryRun,
    };

    return runReturnAchRequest(
      dryRun ? { variables } : { variables, refetchQueries: confirmRefetchQueries, awaitRefetchQueries: true },
    );
  };

  const formConfig: SmartFormConfig<FormValues> = useMemo(
    () => ({
      fieldGroups: [
        {
          name: 'achReturnDetails',
          label: 'ACH Return Inputs',
          description: 'First run a dry preview, then confirm to execute the ACH return.',
          fields: [
            {
              name: 'achRequestId',
              label: 'ACH Request',
              type: 'select',
              options: achRequestOptions,
              placeholder: 'Select ACH request',
              helperText: achRequestHelperText,
              disabled: achRequestOptions.length === 0,
              required: true,
            },
            {
              name: 'returnCode',
              label: 'Return Code',
              placeholder: 'Example: R01',
              helperText: 'Required for Bank Return, optional for Admin Void.',
              required: false,
            },
            {
              name: 'returnType',
              label: 'Return Type',
              type: 'select',
              options: [...RETURN_TYPE_OPTIONS],
              required: true,
            },
            {
              name: 'returnedOn',
              label: 'Returned On',
              type: 'date',
              helperText: 'The day the bank returned the payment — it decides the fee cap.',
              required: true,
              conditional: {
                when: {
                  field: 'returnType',
                  operator: 'equals',
                  value: AchReturnType.BankReturn,
                },
              },
            },
            {
              name: 'notes',
              label: 'Notes',
              placeholder: 'Optional admin notes',
              required: false,
            },
          ],
        },
      ],
      schema,
      submitLabel: 'Preview Return',
      defaultValues: submittedValues ?? {
        achRequestId: achRequestOptions[0]?.value ?? '',
        returnCode: '',
        returnType: AchReturnType.BankReturn,
        returnedOn: '',
        notes: '',
      },
    }),
    [achRequestHelperText, achRequestOptions, submittedValues],
  );

  const handlePreview = (values: FormValues) => {
    const normalizedValues = normalizeFormValues(values);

    void handleMutation({
      mutation: () => runAchReturn(normalizedValues, true),
      extractSuccess: (result) => !!result?.data?.achReturn,
      extractError: (result) => result?.data?.achReturn?.message || 'Unable to preview ACH return',
      debugLabel: 'return-ach-request-preview',
      toast: {
        success: 'ACH return preview generated',
        error: 'Failed to generate ACH return preview',
        loading: 'Generating ACH return preview...',
      },
      onSuccess: (result) => {
        const mutationResult = result.data?.achReturn;

        if (!mutationResult) return;

        setSubmittedValues(normalizedValues);
        setPreviewResult(mutationResult);
        setExecutionResult(null);
        setStep('preview');
      },
    });
  };

  const handleConfirm = () => {
    if (!submittedValues) return;

    void handleMutation({
      mutation: () => runAchReturn(submittedValues, false),
      extractSuccess: (result) => !!result?.data?.achReturn,
      extractError: (result) => result?.data?.achReturn?.message || 'Unable to execute ACH return',
      debugLabel: 'return-ach-request-confirm',
      toast: {
        success: 'ACH return submitted successfully',
        error: 'Failed to submit ACH return',
        loading: 'Submitting ACH return...',
      },
      onSuccess: (result) => {
        const mutationResult = result.data?.achReturn;

        if (!mutationResult) return;

        setExecutionResult(mutationResult);
        setStep('complete');
      },
    });
  };

  const handleStartOver = () => {
    setStep('details');
    setSubmittedValues(null);
    setPreviewResult(null);
    setExecutionResult(null);
  };

  const renderAffectedStatements = (statementIds?: Array<string | null> | null) => (
    <div className="rounded-md border p-4">
      <Typography variant="headerSmall" className="mb-2">
        Affected Statements
      </Typography>
      {(statementIds ?? []).length === 0 && (
        <Typography variant="bodySmall" className="text-muted-foreground">
          No statements affected.
        </Typography>
      )}
      {compact(statementIds).map((statementId) => {
        const statement = statementsById.get(statementId);
        const statementHref = `${routes.loans}/${loanId}/statements/${statementId}`;

        return (
          <div key={statementId} className="mb-3 rounded-md border p-3 last:mb-0">
            <div className="mb-2 flex items-center justify-between gap-2">
              <Typography variant="bodySmall" className="font-medium">
                Statement ID: {statementId}
              </Typography>
              <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs">
                <a href={statementHref} target="_blank" rel="noopener noreferrer" title="Open statement in new tab">
                  <ExternalLink className="h-3 w-3" />
                  Open
                </a>
              </Button>
            </div>
            <SummaryRow
              label="Billing Period"
              value={
                statement
                  ? `${formatDate(statement.billingPeriodStart)} - ${formatDate(statement.billingPeriodEnd)}`
                  : userStatementsLoading
                    ? 'Loading...'
                    : '-'
              }
            />
            <SummaryRow label="Due Date" value={statement ? formatDate(statement.dueDate) : '-'} />
            <SummaryRow label="New Balance" value={statement ? formatCurrencyFromAmount(statement.newBalance) : '-'} />
            <SummaryRow
              label="Minimum Payment Due"
              value={statement ? formatCurrencyFromAmount(statement.minimumPaymentDue) : '-'}
            />
          </div>
        );
      })}
    </div>
  );

  const renderAdjustmentSection = (
    title: string,
    adjustments: Array<AdjustmentItem | null> | null | undefined,
    emptyMessage: string,
  ) => (
    <div className="rounded-md border p-4">
      <Typography variant="headerSmall" className="mb-2">
        {title}
      </Typography>
      {compact(adjustments).length === 0 && (
        <Typography variant="bodySmall" className="text-muted-foreground">
          {emptyMessage}
        </Typography>
      )}
      {compact(adjustments).map((adjustment) => (
        <SummaryRow
          key={`${adjustment.statementId}-${adjustment.effectiveDate}-${adjustment.amountCents}`}
          label={`${adjustment.statementId} (${adjustment.type})`}
          value={`${formatCurrencyFromCents(adjustment.amountCents)} | ${formatDate(adjustment.effectiveDate)}`}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <StepIndicator step={step} />

      {step === 'details' && (
        <SmartForm config={formConfig} onSubmit={handlePreview} loading={loading || achRequestsLoading} />
      )}

      {step === 'preview' && previewResult && (
        <div className="space-y-4">
          <div className="rounded-md border p-4">
            <Typography variant="headerSmall" className="mb-2">
              Preview Summary
            </Typography>
            <SummaryRow label="Message" value={previewResult.message || '-'} />
            <SummaryRow label="Return Code" value={previewResult.returnCode || '-'} />
            <SummaryRow label="Return Reason" value={previewResult.returnReason || '-'} />
            <SummaryRow label="Return Category" value={previewResult.returnCategory || '-'} />
            <SummaryRow label="Return Fee" value={formatReturnFee(previewResult)} />
            <SummaryRow label="Repayment Amount" value={formatCurrencyFromCents(previewResult.repaymentAmountCents)} />
            <SummaryRow label="ACH Request ID" value={previewResult.achRequest?.id || '-'} />
            <SummaryRow label="ACH Request Status" value={previewResult.achRequest?.status || '-'} />
            <SummaryRow
              label="ACH Request Amount"
              value={formatCurrencyFromCents(previewResult.achRequest?.amountCents)}
            />
          </div>

          <div className="rounded-md border p-4">
            <Typography variant="headerSmall" className="mb-2">
              Allocations To Reverse
            </Typography>
            {(previewResult.allocationsToReverse ?? []).length === 0 && (
              <Typography variant="bodySmall" className="text-muted-foreground">
                No allocations to reverse.
              </Typography>
            )}
            {compact<AllocationItem>(previewResult.allocationsToReverse).map((allocation) => (
              <SummaryRow
                key={`${allocation.targetAccountId}-${allocation.amount}`}
                label={allocation.targetAccountId}
                value={String(allocation.amount)}
              />
            ))}
          </div>

          {renderAffectedStatements(previewResult.affectedStatementIds)}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStep('details')} disabled={loading}>
              Back
            </Button>
            <Button onClick={handleConfirm} loading={loading}>
              Confirm Return
            </Button>
          </div>
        </div>
      )}

      {step === 'complete' && executionResult && (
        <div className="space-y-4">
          <div className="rounded-md border p-4">
            <Typography variant="headerSmall" className="mb-2">
              Return Result
            </Typography>
            <SummaryRow label="Message" value={executionResult.message || '-'} />
            <SummaryRow label="Return Code" value={executionResult.returnCode || '-'} />
            <SummaryRow label="Return Reason" value={executionResult.returnReason || '-'} />
            <SummaryRow label="Return Category" value={executionResult.returnCategory || '-'} />
            <SummaryRow label="Return Fee" value={formatReturnFee(executionResult)} />
            <SummaryRow
              label="Repayment Amount"
              value={formatCurrencyFromCents(executionResult.repaymentAmountCents)}
            />
            <SummaryRow label="Allocations Reversed" value={`${executionResult.allocationsReversed ?? '-'}`} />
            <SummaryRow label="Delinquency Triggered" value={executionResult.delinquencyTriggered ? 'Yes' : 'No'} />
          </div>

          {renderAffectedStatements(executionResult.affectedStatementIds)}

          {renderAdjustmentSection(
            'Interest Adjustments',
            executionResult.interestAdjustments,
            'No interest adjustments.',
          )}
          {renderAdjustmentSection('Fee Adjustments', executionResult.feeAdjustments, 'No fee adjustments.')}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={handleStartOver}>
              Return Another ACH Request
            </Button>
            <Button onClick={() => api.close(executionResult)}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
};
